import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { DiningArea, DiningAreasService } from '../dining-areas.service';
import { DiningAreaFormDialogComponent } from '../dining-area-form-dialog/dining-area-form-dialog.component';
import { HasPermissionDirective } from '../../../../../core/directives/has-permission.directive';
import { ConfirmationService } from '../../../../../shared/services/confirmation.service';
import { PageHeaderService } from '../../../../../shared/services/page-header.service';
import { SharedModule } from '../../../../../shared/shared.module';

@Component({
  selector: 'app-dining-areas-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatMenuModule,
    MatDividerModule,
    HasPermissionDirective,
    SharedModule,
  ],
  template: `
    <section class="config-page">
      <app-card variant="default" padding="none" class="config-table-card">
        <div *ngIf="loading" class="loading-container">
          <mat-progress-spinner mode="indeterminate" diameter="50"></mat-progress-spinner>
          <p>Loading dining areas...</p>
        </div>

        <div *ngIf="!loading && items.length === 0" class="empty-state">
          <div class="empty-state__icon"><mat-icon>table_restaurant</mat-icon></div>
          <h3>No dining areas yet</h3>
          <p>Create sections like Ground Floor, Outdoor or VIP Room. Tables belong to an area.</p>
          <button mat-flat-button color="primary" (click)="onCreate()" *appHasPermission="'dining_area.create'">
            <mat-icon>add</mat-icon> Add Dining Area
          </button>
        </div>

        <div *ngIf="!loading && items.length > 0" class="table-container">
          <table mat-table [dataSource]="items" class="modern-table">
            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef>Area</th>
              <td mat-cell *matCellDef="let a">
                <div class="entity-cell">
                  <div class="entity-cell__icon"><mat-icon>table_restaurant</mat-icon></div>
                  <div class="entity-cell__details">
                    <strong class="entity-cell__name">{{ a.name }}</strong>
                    <span class="entity-cell__sub" *ngIf="a.description">{{ a.description }}</span>
                  </div>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="tables">
              <th mat-header-cell *matHeaderCellDef>Tables</th>
              <td mat-cell *matCellDef="let a">{{ a.tables_count ?? 0 }}</td>
            </ng-container>

            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef>Status</th>
              <td mat-cell *matCellDef="let a">
                <span class="status-badge" [class.active]="a.is_active" [class.inactive]="!a.is_active">
                  <span class="status-indicator"></span>{{ a.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef>Actions</th>
              <td mat-cell *matCellDef="let a">
                <button mat-icon-button [matMenuTriggerFor]="menu" matTooltip="More actions" class="action-menu-trigger">
                  <mat-icon>more_vert</mat-icon>
                </button>
                <mat-menu #menu="matMenu" class="action-menu">
                  <button mat-menu-item (click)="onEdit(a)" *appHasPermission="'dining_area.update'">
                    <mat-icon>edit</mat-icon><span>Edit</span>
                  </button>
                  <button mat-menu-item (click)="onToggle(a)" *appHasPermission="'dining_area.update'">
                    <mat-icon>{{ a.is_active ? 'toggle_off' : 'toggle_on' }}</mat-icon>
                    <span>{{ a.is_active ? 'Deactivate' : 'Activate' }}</span>
                  </button>
                  <mat-divider></mat-divider>
                  <button mat-menu-item (click)="onDelete(a)" class="danger-action" *appHasPermission="'dining_area.delete'">
                    <mat-icon>delete</mat-icon><span>Delete</span>
                  </button>
                </mat-menu>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
          </table>

          <mat-paginator
            [length]="total"
            [pageSize]="pageSize"
            [pageIndex]="pageIndex"
            [pageSizeOptions]="pageSizeOptions"
            [showFirstLastButtons]="true"
            aria-label="Dining area pagination"
            (page)="handlePageEvent($event)"
          ></mat-paginator>
        </div>
      </app-card>
    </section>
  `,
  styleUrls: ['../../config-list.shared.scss'],
})
export class DiningAreasListComponent implements OnInit, OnDestroy {
  items: DiningArea[] = [];
  loading = false;
  displayedColumns = ['name', 'tables', 'status', 'actions'];

  total = 0;
  pageIndex = 0;
  pageSize = 25;
  readonly pageSizeOptions = [10, 25, 50, 100];

  constructor(
    private service: DiningAreasService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private confirmationService: ConfirmationService,
    private pageHeader: PageHeaderService,
  ) {}

  ngOnInit(): void {
    this.pageHeader.set({
      title: 'Dining Areas',
      icon: 'table_restaurant',
      backRoute: '/dashboard',
      actions: [
        { label: 'Refresh', icon: 'refresh', variant: 'stroked', permission: 'dining_area.read', disabled: () => this.loading, handler: () => this.load() },
        { label: 'Add Dining Area', icon: 'add', variant: 'primary', permission: 'dining_area.create', handler: () => this.onCreate() },
      ],
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.pageHeader.clear();
  }

  load(): void {
    this.loading = true;
    this.service.getPage({ page: this.pageIndex + 1, perPage: this.pageSize }).subscribe({
      next: (page) => {
        this.items = page.items;
        this.total = page.meta.total;
        this.pageSize = page.meta.perPage;
        this.pageIndex = Math.max(page.meta.currentPage - 1, 0);
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Failed to load dining areas', 'Close', { duration: 3000 });
        this.loading = false;
      },
    });
  }

  handlePageEvent(event: PageEvent): void {
    if (this.pageSize === event.pageSize && this.pageIndex === event.pageIndex) {
      return;
    }
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.load();
  }

  onCreate(): void {
    this.dialog.open(DiningAreaFormDialogComponent, { width: '560px', maxHeight: '90vh', data: {} })
      .afterClosed().subscribe((saved) => { if (saved) this.load(); });
  }

  onEdit(a: DiningArea): void {
    this.dialog.open(DiningAreaFormDialogComponent, { width: '560px', maxHeight: '90vh', data: { id: a.id } })
      .afterClosed().subscribe((saved) => { if (saved) this.load(); });
  }

  onToggle(a: DiningArea): void {
    this.service.toggleActive(a.id).subscribe({
      next: (res) => {
        if (res.success) {
          a.is_active = !a.is_active;
          this.snackBar.open(`Dining area ${a.is_active ? 'activated' : 'deactivated'}`, 'Close', { duration: 3000 });
        }
      },
      error: () => this.snackBar.open('Failed to update status', 'Close', { duration: 3000 }),
    });
  }

  onDelete(a: DiningArea): void {
    this.confirmationService.confirm({
      title: 'Delete dining area',
      message: `Are you sure you want to delete "${a.name}"?`,
      confirmText: 'Delete',
      type: 'danger',
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.service.delete(a.id).subscribe({
        next: (res) => {
          if (res.success) {
            this.snackBar.open('Dining area deleted', 'Close', { duration: 3000 });
            this.load();
          }
        },
        error: (error) => this.snackBar.open(error.error?.message || 'Failed to delete', 'Close', { duration: 5000 }),
      });
    });
  }
}
